import React from "react";
import { Link } from "react-router-dom";
import { LegalLayout, H2, P, UL } from "../components/LegalLayout";
import Seo from "../components/Seo";
import { Sparkles, Globe, Shield, Zap } from "lucide-react";

export default function About() {
    return (
        <>
            <Seo
                path="/about"
                title="About Us — Free AI Writers"
                description="Learn about Free AI Writers — a free directory of 101+ AI writing, image, code, and study tools. Built for bloggers, students, creators, and marketers."
            />
            <LegalLayout
            kicker="The Team"
            title="About Us"
            lastUpdated={null}
        >
            <P>
                <b>Free AI Writers</b> is a free directory of <b>101+ AI tools</b>{" "}
                designed to make creative and professional writing — and a lot
                more — radically accessible. We launched in 2026 with one belief:
                <b> powerful AI shouldn't sit behind paywalls and signup walls</b>.
            </P>

            <H2>What we offer</H2>
            <UL>
                <li>
                    <b>Article & Content Writers</b> — blog posts, paragraphs,
                    meta descriptions, FAQs, press releases
                </li>
                <li>
                    <b>Story & Book Writers</b> — story, character, plot, scene,
                    backstory generators
                </li>
                <li>
                    <b>Poem & Lyrics Writers</b> — poems, rap lyrics, haikus,
                    rhymes, diss tracks
                </li>
                <li>
                    <b>Email & Letter Writers</b> — cover letters, sales emails,
                    love letters, thank-you notes
                </li>
                <li>
                    <b>Rewriting & Editing</b> — paragraph rewriter, essay
                    extender, AI summarizer
                </li>
                <li>
                    <b>Business & Professional</b> — LinkedIn summaries, job
                    descriptions, taglines, speeches
                </li>
                <li>
                    <b>Education & Study Tools</b> — essay writer, math solver,
                    notes maker, research paper writer
                </li>
                <li>
                    <b>Creative & Fun</b> — pick-up lines, roasts, recipes,
                    TikTok comments
                </li>
                <li>
                    <b>Coding & Programming</b> — multi-language code generator,
                    Python helper
                </li>
                <li>
                    <b>Text-to-Image Tools</b> — AI image generator and editor
                    powered by Nano Banana
                </li>
            </UL>

            <H2>Powered by the world's best AI</H2>
            <P>
                Every tool is powered by the latest models — including{" "}
                <b>OpenAI GPT-5.2, Google Gemini 3, Anthropic Claude Sonnet 4.5</b>
                , and <b>Gemini Nano Banana</b> for image generation. You can
                pick the model that suits your task.
            </P>

            <H2>Our promise</H2>
            <div className="grid sm:grid-cols-2 gap-4 not-prose">
                {[
                    {
                        icon: Zap,
                        title: "Always free",
                        desc: "No paywalls, no signup, no credit card.",
                    },
                    {
                        icon: Shield,
                        title: "Privacy first",
                        desc: "We don't sell your data and don't train on your prompts.",
                    },
                    {
                        icon: Globe,
                        title: "Built for everyone",
                        desc: "Students, writers, marketers, developers, creators.",
                    },
                    {
                        icon: Sparkles,
                        title: "Always improving",
                        desc: "We add new tools every month. Suggestions welcome!",
                    },
                ].map((f) => (
                    <div
                        key={f.title}
                        className="nb-border rounded-2xl bg-[#FDE047] p-4 nb-shadow-sm flex items-start gap-3"
                    >
                        <div className="w-9 h-9 rounded-lg nb-border bg-white flex items-center justify-center shrink-0">
                            <f.icon strokeWidth={3} className="w-4 h-4" />
                        </div>
                        <div>
                            <div className="font-display font-black text-lg">
                                {f.title}
                            </div>
                            <div className="text-sm text-neutral-800 mt-0.5">
                                {f.desc}
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            <H2>Get in touch</H2>
            <P>
                Got a feature request, a bug report, or want to partner with us?
                Visit our{" "}
                <Link to="/contact" className="underline font-bold">
                    contact page
                </Link>{" "}
                or email <b>freeaiwriters@gmail.com</b>.
            </P>

            <H2>Start exploring</H2>
            <P>
                Ready to write something amazing?{" "}
                <Link to="/" className="underline font-bold">
                    Browse all 101+ free AI tools →
                </Link>
            </P>
        </LegalLayout>
        </>
    );
}
